import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { globalSearch } from "@/lib/api";
import { Search as SearchIcon } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const TYPE_LABEL = {
  books: "Books", articles: "Articles", research_themes: "Research Themes", events: "Events", videos: "Videos",
};

export default function SearchPage() {
  useDocumentTitle("Search");
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState({ groups: {}, total: 0 });
  const [filters, setFilters] = useState({ series: "", language: "", subject: "", types: "" });

  const activeParams = useMemo(
    () => Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    [filters],
  );

  useEffect(() => {
    if (!q) return;
    let cancelled = false;
    globalSearch({ q, ...activeParams })
      .then((data) => { if (!cancelled) setResults(data); })
      .catch(() => { /* silent; empty results render appropriately */ });
    return () => { cancelled = true; };
  }, [q, activeParams]);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <div data-testid="search-page">
      <section className="container-wide pt-16 pb-8">
        <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Search</div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl leading-[1.05]" style={{ color: "var(--gi-maroon)" }}>Results for “{q}”</h1>
        <div className="hair-divider mt-6 max-w-md" />
        <form className="mt-6 flex items-center gap-3 border px-3 max-w-2xl bg-white" style={{ borderColor: "var(--gi-border)" }} onSubmit={(e) => { e.preventDefault(); const val = new FormData(e.target).get("q"); setParams({ q: val }); }}>
          <SearchIcon size={16} style={{ color: "var(--gi-muted)" }} />
          <input name="q" defaultValue={q} className="w-full py-3 outline-none text-sm" placeholder="Search again" data-testid="search-page-input" />
          <button className="btn-ghost">Search</button>
        </form>
      </section>

      <section className="container-wide pb-24 grid gap-10 lg:grid-cols-[260px_1fr]">
        <aside data-testid="search-filters">
          <FilterBlock title="Series" value={filters.series} onChange={v => set("series", v)} options={["Glories of India Series", "Green Guru Knowledge Series"]} testPrefix="search-filter-series" />
          <FilterBlock title="Content type" value={filters.types} onChange={v => set("types", v)} options={["books", "articles", "research_themes", "events", "videos"]} testPrefix="search-filter-type" />
          <FilterBlock title="Language" value={filters.language} onChange={v => set("language", v)} options={["English", "Hindi", "Bengali"]} testPrefix="search-filter-lang" />
          <FilterBlock title="Subject" value={filters.subject} onChange={v => set("subject", v)} options={["Horticulture", "Gardening", "Landscaping", "Palms", "Environment", "History and Civilization", "Archaeology", "Comparative Religion", "Comparative Civilizations", "Public Awareness"]} testPrefix="search-filter-subject" />
        </aside>

        <div>
          {results.total === 0 && <div className="py-16 text-center" data-testid="search-empty">
            <div className="font-heading text-2xl" style={{ color: "var(--gi-maroon)" }}>No matches</div>
            <p className="mt-3 text-sm" style={{ color: "var(--gi-muted)" }}>Try a different search term or clear the filters.</p>
          </div>}
          {Object.entries(results.groups || {}).map(([type, group]) => (
            <section key={type} className="mb-14" data-testid={`search-group-${type}`}>
              <div className="flex items-center gap-3">
                <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)", letterSpacing: "0.22em" }}>{TYPE_LABEL[type] || type}</div>
                <span className="text-xs" style={{ color: "var(--gi-muted)" }}>{group.count} result{group.count > 1 ? "s" : ""}</span>
              </div>
              <div className="mt-6 space-y-6">
                {group.hits.map(h => (
                  <Link key={h.id} to={h.url} className="block border bg-white p-6 hover:border-[var(--gi-maroon)]" style={{ borderColor: "var(--gi-border)" }} data-testid={`search-hit-${h.id}`}>
                    <div className="font-accent text-[9px]" style={{ color: "var(--gi-gold)" }}>{h.subtitle || h.type}{h.series ? ` · ${h.series}` : ""}</div>
                    <div className="mt-2 font-heading text-2xl leading-tight" style={{ color: "var(--gi-charcoal)" }}>{h.title}</div>
                    {h.summary && <p className="mt-2 text-sm" style={{ color: "var(--gi-muted)" }}>{h.summary.slice(0, 220)}</p>}
                    {h.editions && <div className="mt-3 text-[11px]" style={{ color: "var(--gi-muted)" }}>Editions: {h.editions.join(", ")}</div>}
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      </section>
    </div>
  );
}

const FilterBlock = ({ title, value, onChange, options, testPrefix }) => (
  <div className="mb-8">
    <div className="font-accent text-[10px] mb-3" style={{ color: "var(--gi-maroon)" }}>{title}</div>
    <div className="space-y-2">
      {options.map(o => (
        <button key={o} onClick={() => onChange(value === o ? "" : o)} className="block text-left w-full text-sm py-1.5" style={{ color: value === o ? "var(--gi-maroon)" : "var(--gi-charcoal-2)" }} data-testid={`${testPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <span className="inline-block w-4">{value === o ? "◆" : "◇"}</span> {o}
        </button>
      ))}
    </div>
  </div>
);
